import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Cookie, SlidersHorizontal } from "lucide-react";
import { useCookieConsent } from "../context/CookieConsentContext";
import { Button } from "./ui/button";

function PreferenceModal({ analytics, marketing, onSave, onClose }) {
  const [a, setA] = useState(analytics);
  const [m, setM] = useState(marketing);

  useEffect(() => {
    setA(analytics);
    setM(marketing);
  }, [analytics, marketing]);

  return (
    <div
      className="fixed inset-0 z-[10050] flex items-end justify-center bg-black/45 p-3 sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-prefs-title"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-zinc-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-zinc-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5 text-[#190276]" aria-hidden />
            <h2 id="cookie-prefs-title" className="text-lg font-bold text-zinc-900">
              Préférences cookies
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-5 px-5 py-5 text-sm text-zinc-700">
          <p className="leading-relaxed text-zinc-600">
            Les cookies strictement nécessaires au fonctionnement du site (session, panier, authentification,
            préférences de consentement) sont toujours utilisés. Vous pouvez activer ou refuser les autres
            catégories ci-dessous.
          </p>
          <div className="rounded-xl border border-zinc-200 bg-zinc-50/80 p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="font-semibold text-zinc-900">Nécessaires</span>
              <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">Toujours actifs</span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-zinc-600">
              Authentification, magasin sélectionné, panier invité, mémorisation de votre choix sur les cookies.
            </p>
          </div>
          <label className="flex cursor-pointer flex-col gap-2 rounded-xl border border-zinc-200 p-4 transition-colors hover:border-[#190276]/30">
            <div className="flex items-center justify-between gap-3">
              <span className="font-semibold text-zinc-900">Mesure d&apos;audience</span>
              <input
                type="checkbox"
                checked={a}
                onChange={(e) => setA(e.target.checked)}
                className="h-4 w-4 rounded border-zinc-300 text-[#190276] focus:ring-[#190276]"
              />
            </div>
            <p className="text-xs leading-relaxed text-zinc-600">
              Outil d&apos;analyse (PostHog) pour comprendre l&apos;usage du site. Activé uniquement si vous
              l&apos;acceptez.
            </p>
          </label>
          <label className="flex cursor-pointer flex-col gap-2 rounded-xl border border-zinc-200 p-4 opacity-90">
            <div className="flex items-center justify-between gap-3">
              <span className="font-semibold text-zinc-900">Marketing</span>
              <input
                type="checkbox"
                checked={m}
                onChange={(e) => setM(e.target.checked)}
                className="h-4 w-4 rounded border-zinc-300 text-[#190276] focus:ring-[#190276]"
              />
            </div>
            <p className="text-xs leading-relaxed text-zinc-600">
              Réservé aux futurs traceurs publicitaires ou réseaux sociaux. Aucun pixel marketing n&apos;est
              déployé dans le code actuel du site ; cette option prépare une activation ultérieure encadrée.
            </p>
          </label>
          <p className="text-xs text-zinc-500">
            <Link to="/politique-cookies" className="font-semibold text-[#190276] underline-offset-2 hover:underline">
              Politique cookies
            </Link>
            {" · "}
            <Link
              to="/politique-confidentialite"
              className="font-semibold text-[#190276] underline-offset-2 hover:underline"
            >
              Confidentialité
            </Link>
          </p>
        </div>
        <div className="flex flex-col-reverse gap-2 border-t border-zinc-100 px-5 py-4 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" className="h-11 rounded-xl" onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="button"
            className="h-11 rounded-xl bg-[#190276] font-semibold hover:bg-[#130160]"
            onClick={() => onSave(a, m)}
          >
            Enregistrer mes choix
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function CookieConsentBanner() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const {
    hasDecision,
    analytics,
    marketing,
    prefsOpen,
    openPreferences,
    closePreferences,
    acceptAll,
    refuseAll,
    saveCustom,
  } = useCookieConsent();

  if (isAdmin) return null;

  const showBar = !hasDecision;

  return (
    <>
      {prefsOpen && (
        <PreferenceModal
          analytics={analytics}
          marketing={marketing}
          onSave={saveCustom}
          onClose={closePreferences}
        />
      )}

      {showBar && (
        <div
          className="fixed inset-x-0 bottom-0 z-[10050] border-t border-zinc-200/80 bg-white/95 shadow-[0_-8px_40px_rgba(0,0,0,0.12)] backdrop-blur-md"
          role="region"
          aria-label="Informations sur les cookies"
        >
          <div className="mx-auto flex max-w-[min(100%,1580px)] flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:gap-6 md:px-6 md:py-5 xl:px-8">
            <div className="flex min-w-0 flex-1 gap-3">
              <div className="hidden shrink-0 sm:block">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#190276]/10 text-[#190276]">
                  <Cookie className="h-5 w-5" aria-hidden />
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-zinc-900">Cookies et données</p>
                <p className="mt-1 text-sm leading-relaxed text-zinc-600">
                  Nous utilisons des cookies et stockages locaux nécessaires au site (compte, panier, magasin).
                  Avec votre accord, nous activons aussi la mesure d&apos;audience (PostHog). Consultez notre{" "}
                  <Link
                    to="/politique-cookies"
                    className="font-semibold text-[#190276] underline-offset-2 hover:underline"
                  >
                    politique cookies
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <Button
                type="button"
                variant="outline"
                className="h-11 rounded-xl border-zinc-300"
                onClick={openPreferences}
              >
                Personnaliser
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-11 rounded-xl border-zinc-300"
                onClick={refuseAll}
              >
                Refuser
              </Button>
              <Button
                type="button"
                className="h-11 rounded-xl bg-[#190276] px-5 font-semibold hover:bg-[#130160]"
                onClick={acceptAll}
              >
                Tout accepter
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
